/* ══════════════════════════════════════════════════════
   TIER DEFINITIONS — Subscription levels and feature gates
   ══════════════════════════════════════════════════════ */

export const TIERS = {
  free: {
    price: 0,
    label: 'Starter',
    color: 'var(--mut)',
    features: [
      'Manual entry + receipt scanner',
      '1 budget strategy (50/30/20)',
      'Health Score + daily streak',
      'Financial journal',
      'Community (browse only)',
      'Bill tracker',
      '1 savings goal',
    ],
    limits: { strategies: 1, goals: 1, aiCoach: false, business: false, plaid: false, square: false, family: false, planner: false },
  },
  personal: {
    price: 6.99,
    priceYearly: 69,
    label: 'Personal',
    color: 'var(--gold)',
    features: [
      'All 10 budget strategies',
      'Unlimited goals + Savings Buckets',
      'AI Financial Coach',
      'Plaid bank sync (or stay manual)',
      'Net worth tracker',
      'Debt payoff planner (avalanche / snowball)',
      'Full community + journal',
      'CSV / PDF import',
    ],
    limits: { strategies: 10, goals: 99, aiCoach: true, business: false, plaid: true, square: false, family: false, planner: false },
  },
  business: {
    price: 13.99,
    priceYearly: 139,
    label: 'Business',
    color: '#8FC89A',
    features: [
      'Everything in Personal',
      'Business dashboard + P&L',
      'Stripe Connect (auto-sync revenue)',
      'Client planner + ledger',
      'Service pricing calculator',
      'Profit per service report',
      'Quarterly tax reserve (25% auto)',
      'Schedule C export',
    ],
    limits: { strategies: 10, goals: 99, aiCoach: true, business: true, plaid: true, square: true, family: false, planner: false },
  },
  planner: {
    price: 24.99,
    priceYearly: 249,
    label: 'Planner',
    color: '#D4B254',
    features: [
      'Everything in Business',
      'Tax-aware net worth (pre-tax / Roth / taxable / HSA)',
      'Roth conversion ladder + IRMAA alerts',
      'RSU / ISO / ESPP vest + AMT modeling',
      'Fire Drill mode (job-loss scenarios)',
      'Scenario tree — branching what-ifs',
      'Planner-voiced AI Coach',
      'Beneficiary + estate-lite audit',
    ],
    limits: { strategies: 10, goals: 99, aiCoach: true, business: true, plaid: true, square: true, family: false, planner: true },
  },
  gold: {
    price: 17.99,
    priceYearly: 179,
    label: 'Gold',
    color: '#C9A96E',
    features: [
      'Everything in Business',
      'Partner dashboards (2 people)',
      'Shared goals & expenses',
      'Square payment sync',
      'GlossGenius integration',
      'Priority support',
      'Advanced reports',
    ],
    limits: { strategies: 10, goals: 99, aiCoach: true, business: true, plaid: true, square: true, family: true, maxProfiles: 2, planner: false },
  },
};

// Canonical order. Landing page renders `LANDING_TIERS` below — `gold` is kept
// in this list for backward compatibility with existing subscribers but is not
// shown to new signups. `household` is sold as a +$5/mo add-on, not a tier.
export const TIER_ORDER = ['free', 'personal', 'business', 'planner', 'gold'];

// What the new landing page shows as paid plans (in column order).
export const LANDING_TIERS = ['free', 'personal', 'business', 'planner'];

export const HOUSEHOLD_ADDON = {
  price: 5,
  priceYearly: 50,
  label: 'Family add-on',
  description: 'Add up to 5 profiles with shared goals and Us View. Stacks on any paid plan.',
};

export function tierAtLeast(currentTier, requiredTier) {
  return TIER_ORDER.indexOf(currentTier) >= TIER_ORDER.indexOf(requiredTier);
}

/**
 * Single source of truth for whether merchant memory + tax-deduction
 * surfaces (Dashboard widget, Business → Review tab, matcher invocation)
 * should be active for this user.
 *
 * Pass the combined state object (e.g., { userStore: useUserStore.getState() }).
 */
export function isMerchantMemoryEnabled(state) {
  return state?.userStore?.isBusiness === true;
}
